import { useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import DeleteIcon from '@material-ui/icons/Delete'
import { process } from '@progress/kendo-data-query'
import { Grid, GridColumn } from '@progress/kendo-react-grid'

import { Colors } from '~/styles'
import { Images } from '~/assets'
import { ServiceRequestAPI, SettlementAPI } from '~/api'
import { Alerts, CloudIcon, FilterIcon, MasterModal, ModalDel } from '~/components'
import { ActionCell, ActionContainer } from '../../settlement.styled'
import { ConvertLocalTimeGet, Lookup, prefix, insertLog } from '../../../../utils'
import { Container, TableData, TransparentInput, UploadButton, UploadDocument, WrappreFilter } from './styled.add-document'

const CustomFilterCell = ({ onChange, value, field }) => {
  const handleChangeFilter = (e) => {
    onChange({
      operator: 'contains',
      value: e.target.value,
      syntheticEvent: e.syntheticEvent,
    })
  }
  return (
    <WrappreFilter>
      <TransparentInput
        id={`filter-input-${field}`}
        type='text'
        onChange={handleChangeFilter}
        value={value}
      />
      <FilterIcon />
    </WrappreFilter>
  )
}


const AddDocument = ({
  data,
  dataSr,
  taskCode,
  inEditMode,
  getActivity,
  handleClose,
  activityCode,
}) => {
  const [fileSize, setFileSize] = useState(0)
  const [gridState, setGridState] = useState({
    skip: 0,
    sort: [],
    take: null,
    filter: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState()
  const [showAlert, setAlert] = useState({ visible: false, msg: '', success: false })
  const [showModalDelete, setShowModalDelete] = useState(false)

  const dataUserProfile = useSelector((state) => state.usersProfileReducer)
  const formatFileSize = (sizeInBytes) => {
    return (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB'
  }

  const attachments = useMemo(() => {
    return data && data.length ? data.map((item) => ({
      ...item,
      filename: item.filename,
      unformated_filesize: item.filesize,
      filesize: formatFileSize(item.filesize),
      created_date: ConvertLocalTimeGet(dataSr.Location, item.created_date, 'DD/MM/YYYY HH:mm'),
      created_by: item.created_by,
    })) : []
  }, [data, dataSr.Location])

  const getFileIcon = (props) => {
    const filename = props.dataItem.filename
    const extension = filename?.split('.')[filename?.split('.').length - 1]?.toLowerCase()

    if (extension === 'doc' || extension === 'docx') {
      return <img width={24} height={24} src={Images.filesIconDoc} />
    }
    if (extension === 'xls' || extension === 'xlsx') {
      return <img width={24} height={24} src={Images.filesIconXls} />
    }
    if (extension === 'pdf') {
      return <img width={24} height={24} src={Images.filesIconPdf} />
    }
    if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') {
      return <img width={24} height={24} src={Images.filesIconImg} />
    }
  }

  const onDataStateChange = (e) => {
    setGridState(e.dataState)
  }

  const handleDeleteDocument = async (dataItem) => {
    if (inEditMode) {
      const attachmentPayload = {
        activity_code: activityCode,
        filename: dataItem.filename,
        filesize: dataItem.unformated_filesize,
        attachment_type: 'document',
        path: dataItem.path,
        code: dataItem.code,
        task_header_code: taskCode,
        is_active: false,
        created_by: dataUserProfile.Email,
        modified_by: dataUserProfile.Email,
        is_deleted: true,
        created_date: new Date(),
        modified_date: new Date(),
      }
      try {
        const response = await SettlementAPI.deleteActivityTaskAttachment(attachmentPayload)
        if (response && response.result) {
          setAlert({
            msg: 'Document Deletion has been successful',
            success: true,
            visible: true,
          })
        } else {
          insertLog({
            message: response.error?.message || 'Failed to delete document',
            action: Lookup.LOG_ACTION.DELETE,
            refNo: taskCode || '',
            module: Lookup.LOG_MODULE.EXECUTION,
            submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
          })
          setAlert({ msg: 'Cannot Delete Document', success: false, visible: true })
        }
      } catch (error) {
        console.log(error)
      }
      setSelectedDocument(undefined)
      getActivity()
      handleClose()
    }
    setShowModalDelete(false)
  }


  const handleFileUpload = async (e) => {
    setIsLoading(true)
    const fileSizeLimit = fileSize * 1024 * 1024
    let isLimitExceeded = false
    const formData = new FormData()
    const files = e.target.files
    for (let i = 0; i < files.length; i++) {
      const v = files[i]
      v.size > fileSizeLimit ? (isLimitExceeded = true) : null
      formData.append('file', v)
    }
    if (isLimitExceeded) {
      setAlert({ msg: `One of the file size exceed ${fileSize} Mb`, success: false, visible: true })
      insertLog({
        message: `One of the file size exceed ${fileSize} Mb`,
        action: Lookup.LOG_ACTION.UPLOAD,
        refNo: taskCode || '',
        module: Lookup.LOG_MODULE.EXECUTION,
        submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
      })
      setIsLoading(false)
    } else {
      SettlementAPI.UploadAttachments(formData)
        .then(async (res) => {
          const data = res.data.data
          for (let i = 0; i < data.length; i++) {
            const attachment = data[i]
            const attachmentPayload = {
              activity_code: activityCode,
              filename: attachment.FileName,
              filesize: Number(attachment.FileSize),
              attachment_type: 'document',
              path: attachment.Path,
              code: taskCode,
              task_header_code: taskCode,
              is_active: true,
              created_by: dataUserProfile.Email,
              modified_by: dataUserProfile.Email,
              is_deleted: false,
              created_date: new Date(),
              modified_date: new Date(),
            }
            try {
              await SettlementAPI.insertActivityTaskAttachment(attachmentPayload)
            } catch (error) {
              console.log(error)
              insertLog({
                message: error?.message || 'Failed to upload attachment',
                action: Lookup.LOG_ACTION.UPLOAD,
                refNo: taskCode || '',
                module: Lookup.LOG_MODULE.EXECUTION,
                submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
              })
            }
          }
          getActivity()
          handleClose()
          setIsLoading(false)
        })
        .catch((err) => {
          console.log(err)
          insertLog({
            message: err?.message || 'Failed to upload attachment',
            action: Lookup.LOG_ACTION.UPLOAD,
            refNo: taskCode || '',
            module: Lookup.LOG_MODULE.EXECUTION,
            submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
          })
          setIsLoading(false)
        })
    }
  }

  const getFileSize = async () => {
    try {
      const filter =
        '[{"field":"type","type":"text","value":"File_Size"},{"field":"is_active","type":"bool","value":"true"}]'
      const { data } = await ServiceRequestAPI.GetAllFromTableEnum(0, 2, filter)
      setFileSize(parseInt(data[0]?.Value))
    } catch (error) {
      console.log(error)
    }
  }

  const downloadFileFromServer = async (props) => {
    try {
      let response = null
      if (props.dataItem.path.toLowerCase().includes('lbs-attachment')) {
        response = await SettlementAPI.GetAttachmentFileSR(props.dataItem.filename)
      } else {
        response = await SettlementAPI.GetAttachmentFile(props.dataItem.filename)
      }
      const baseFile64 = response.data
      const prefixBase64 = prefix(props.dataItem.filename)
      const dataBase64 = prefixBase64 + baseFile64

      const a = document.createElement('a')
      a.href = dataBase64
      a.download = props.dataItem.filename
      document.body.appendChild(a)
      a.click() // <-- This triggers the download
      document.body.removeChild(a) // <-- Clean up
    } catch (err) {
      console.log(err)
    }
  }

  const getAll = async () => {
    await getFileSize()
  }

  useEffect(() => {
    getAll()
  }, [])

  const noCell = (props) => (
    <TableData>
      {props.dataIndex + 1}
    </TableData>
  )

  const fileNameCell = (props) => (
    <TableData>
      {getFileIcon(props)} {props.dataItem.filename}
    </TableData>
  )

  const actionCell = (props) => (
    <TableData>
      <ActionContainer>
        <ActionCell
          id='settlement-act-doc_mdl-action-delete-td'
          onClick={() => {
            if (inEditMode) {
              setSelectedDocument(props.dataItem)
              setShowModalDelete(true)
            }
          }}
        >
          <DeleteIcon htmlColor={inEditMode ? Colors.red : Colors.softGrey} />
        </ActionCell>
        <ActionCell
          id='settlement-act-doc_mdl-action-download-td'
          onClick={(e) => {
            e.preventDefault()
            downloadFileFromServer(props)
          }}
        >
          <CloudIcon />
        </ActionCell>
      </ActionContainer>
    </TableData>
  )

  return (
    <MasterModal
      showModal
      id='settlement-act-doc_mdl'
      title='View Documents'
      isDetail
      width='80%'
      hasSubtitle={false}
      handleClose={handleClose}>
      {showModalDelete && (
        <ModalDel
          submitDelete={() => handleDeleteDocument(selectedDocument)}
          deleteText='Are you sure to delete the data ?'
          toggleDialog={() => {
            setShowModalDelete(false)
          }}
        />
      )}
      {showAlert.visible && (
        <Alerts
          id='settlement-alert'
          msg={showAlert.msg}
          close={() => setAlert({ msg: '', success: true, visible: false })}
          open={showAlert.visible}
          success={showAlert.success}
        />
      )}
      <Container>
        {inEditMode && (
          <UploadDocument>
            <UploadButton
              disabled={isLoading}
              onClick={() => document.getElementById('settlement-act-doc_mdl-upload-input-file').click()}
            >
              Upload File
            </UploadButton>
            <input
              id='settlement-act-doc_mdl-upload-input-file'
              multiple
              type='file'
              name='settlement-act-docs-attacments'
              style={{ display: 'none' }}
              onChange={(e) => {
                handleFileUpload(e)
                e.target.value = null
              }}
            // accept={fileType}
            />
          </UploadDocument>
        )}
        <Grid
          filterable
          {...gridState}
          pageable={false}
          data={process(attachments, gridState)}
          dataItemKey='Filename'
          onDataStateChange={onDataStateChange}
        >
          <GridColumn filterable={false} width={100} field='no' title='No' cell={noCell} />
          <GridColumn filterCell={CustomFilterCell} filterable filter='text' width={350} field='filename' title='Document Name' cell={fileNameCell} />
          <GridColumn filterCell={CustomFilterCell} filterable filter='text' width={150} field='filesize' title='File Size' />
          <GridColumn filterCell={CustomFilterCell} filterable filter='text' width={250} field='created_date' title='Upload time' />
          <GridColumn filterCell={CustomFilterCell} filterable filter='text' width={250} field='created_by' title='Uploaded By' />
          <GridColumn filterable={false} width={100} field='Action' title='Action' cell={actionCell} />
        </Grid>
      </Container>
    </MasterModal>
  )
}

export default AddDocument
